import { Injectable } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/auth";
import { BehaviorSubject, EMPTY, from, Observable, of, throwError } from "rxjs";
import { SocialType } from "src/app/auth/social-type";
import { WindowService } from "src/service/window.service";
import { socialProviders } from "./social-providers";
import firebase from "firebase";
import { EmailCredentials } from "./credentials";
import { map, switchMap, tap } from "rxjs/operators";
import { RestApiService } from "src/service/rest-api.service";
import { HttpHeaders } from "@angular/common/http";
import { JwtObj, PublicJwtObj } from "./jwt-obj";
import { AppInitService, ServerType } from "src/service/app-init.service";
import { UserService } from "src/service/user.service";
import { CartService } from "../dashboard/cart/cart.service";
import { DialogService } from "src/service/dialog.service";
import { DialogImage } from "../data/dialog-image";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  constructor(
    private auth: AngularFireAuth,
    private windowService: WindowService,
    private restApiService: RestApiService,
    private appInitService: AppInitService,
    private userService: UserService,
    private cartService: CartService,
  //  private dialogService: DialogService
  ) {
    this.auth.authState.subscribe((user) => {
      this.firebaseUser.next(user);
      if (!user) {
        localStorage.setItem("jwt", null);
      }
    });
  }

  facebookProviderId = "facebook.com";
  interruptedUser = new BehaviorSubject<firebase.User>(null);
  firebaseUser = new BehaviorSubject<firebase.User>(null);

  get isLoggedIn(): boolean {
    const jwtObj: JwtObj = JSON.parse(localStorage.getItem("jwt"));
    if (!jwtObj) {
      return false;
    }
    if (!jwtObj.expiration || Date.now() > jwtObj.expiration) {
      this.deleteJwt();
      return false;
    } else  {
      return true;
    }
  }

  getIdToken = () => from(firebase.auth().currentUser.getIdToken(true));

  signInWithSocial(socialType: SocialType) {
    return from(
      this.auth.signInWithPopup(
        socialProviders
          .find((item) => item.socialType === socialType)
          .getProvider()
      )
    ).pipe(
      switchMap((firebaseResponse) =>
        this.processSignInResponse(firebaseResponse)
      )
    );
  }

  isExist(firebaseReponse: firebase.auth.UserCredential) {
    return this.userService
      .isExist(firebaseReponse.user.uid)
      .pipe(map((isExist) => [firebaseReponse, isExist]));
  }

  handleAfterLoginOrSignupRouting(firebaseResponse, login) {
    return this.isExist(firebaseResponse).pipe(
      switchMap(([firebaseResponse, isExist]) =>
        this.getNavigateRouteAfterSignupOrLogin(
          firebaseResponse,
          isExist,
          login
        )
      )
    );
  }

  getNavigateRouteAfterSignupOrLogin(
    firebaseResponse: firebase.auth.UserCredential,
    isExist,
    login
  ) {
    if (!firebaseResponse.user.emailVerified && !login && !this.isFacebook()) {
      return this.sendEmailVerification().pipe(
      /*   switchMap(() =>
          this.dialogService.showSuccessDialog(
            "EMAIL_VERIFICATION_SENT",
            DialogImage.EMAIL_SENT
          )
        ), */
        switchMap(() => of({route:["/"], emailSentEvent:true}))
      );
    }

    if (!firebaseResponse.user.emailVerified && !this.isFacebook() && login) {
      window.alert("Verify your email");
      return of({route:["/"], emailSentEvent:false});
    } else {
      if (firebaseResponse.user.phoneNumber && isExist) {
        return of({route:["/"], emailSentEvent:false});

      } else if (firebaseResponse.user.phoneNumber) {
        this.interruptedUser.next(firebaseResponse.user);
        return of({route:["/create-user/create/verification"], emailSentEvent:false});
      } else


        return of({route:["/create-user"], emailSentEvent:false});
    }
  }

  isFacebook() {
    return (
      this.firebaseUser.value.providerId == this.facebookProviderId ||
      this.firebaseUser.value.providerData
        .map((item) => item.providerId)
        .includes(this.facebookProviderId)
    );
  }

  processSignInResponse(firebaseResponse: firebase.auth.UserCredential) {
    return firebaseResponse.user.phoneNumber
      ? this.getJwt(firebaseResponse.user.uid).pipe(
          switchMap(() => of(firebaseResponse))
        )
      : of(firebaseResponse);
  }

  signInWithEmail(emailCredentials: EmailCredentials) {
    return from(
      this.auth.signInWithEmailAndPassword(
        emailCredentials.email,
        emailCredentials.password
      )
    ).pipe(
      switchMap((firebaseResponse) =>
        this.processSignInResponse(firebaseResponse)
      )
    );
  }

  getSelfUrl() {
    return this.appInitService.SELF_URL;
  }

  verifyEmail(oobCode) {
    return from(this.auth.applyActionCode(oobCode));
  }

  registerWithEmail(emailCredentials: EmailCredentials) {
    return from(
      this.auth.createUserWithEmailAndPassword(
        emailCredentials.email,
        emailCredentials.password
      )
    );
  }

  resetPassword(email: string) {
    return from(this.auth.sendPasswordResetEmail(email));
  }

  verifyResetPassword(code, newPassword) {
    // this.auth.verifyPasswordResetCode(code);
    return from(this.auth.confirmPasswordReset(code, newPassword));
  }

  sendSmsCode(phone, recaptchaVerifier) {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) return EMPTY;

    return from(
      currentUser.phoneNumber
        ? firebase.auth().signInWithPhoneNumber(phone, recaptchaVerifier)
        : currentUser.linkWithPhoneNumber(phone, recaptchaVerifier)
    );
  }

  sendEmailVerification() {
    return from(
      firebase.auth().currentUser.sendEmailVerification({
        url: `${this.getSelfUrl()}create-user`,
      })
    );
  }

  getJwt(uid: string) {
    return this.getIdToken().pipe(
      switchMap((firebaseToken) =>
        this.restApiService.get(ServerType.AUTH, "jwt", {
          headers: new HttpHeaders().set("x-id-token", firebaseToken),
        })
      ),
      tap((jwtObj: JwtObj) => this.saveJwt({ ...jwtObj, uid: uid }))
    );
  }

  saveJwt = (jwtObj: JwtObj) => {
    localStorage.setItem("jwt", JSON.stringify(jwtObj));
  };

  fetchJwt = (): JwtObj => JSON.parse(localStorage.getItem("jwt"));

  deleteJwt = () => localStorage.removeItem("jwt");

  verifySmsCode(code: string) {
    return this.windowService.windowRef.confirmationResult
      ? from(this.windowService.windowRef.confirmationResult.confirm(code))
      : throwError("confirmation result is null");
  }

  signOut = () =>
    from(this.auth.signOut()).pipe(
      tap(() => {
        this.userService.user.next(null);
        this.deleteJwt();
        this.disposeCart();
      })
    );

  disposeCart() {
    this.cartService.cartList.next(null);
    localStorage.removeItem("cart");
  }

  syncPublicJwt(): Observable<PublicJwtObj> {
    const publicJwtObj: PublicJwtObj = this.getPublicJwt();
    if (publicJwtObj != null) {
      if (Date.now() > publicJwtObj.expiration) {
        this.deletePublicJwt();
      } else {
        return of(publicJwtObj);
      }
    }
    return this.restApiService
      .get(ServerType.AUTH, "pb-jwt")
      .pipe(
        tap((publicJwtObj: PublicJwtObj) => this.savePublicJwt(publicJwtObj))
      );
  }

  savePublicJwt(publicJwtObj: PublicJwtObj) {
    localStorage.setItem("publicJwt", JSON.stringify(publicJwtObj));
  }

  deletePublicJwt = () => localStorage.removeItem("publicJwt");

  getPublicJwt = () => JSON.parse(localStorage.getItem("publicJwt"));

  changeEmail(newEmail: string) {
    return from(this.firebaseUser.value.updateEmail(newEmail)).pipe(
      map(() => newEmail)
    );
  }

  reauthenticate(password) {
    const credential = firebase.auth.EmailAuthProvider.credential(
      this.firebaseUser.value.email,
      password
    );

    return from(
      this.firebaseUser.value.reauthenticateWithCredential(credential)
    );
    /*  return from(
      this.firebaseUser.value.reauthenticateWithPopup(
        new firebase.auth.GoogleAuthProvider()
      )
    ); */
  }

  checkUserCreatedWithEmail() {
    return this.firebaseUser.pipe(
      map((user) =>
        user?.providerData.map((item) => item.providerId).includes("password")
      )
    );
  }

  changePhoneNumberSendSms(phoneNumber, recaptchaVerifier) {
    const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
    return from(
      phoneAuthProvider.verifyPhoneNumber(phoneNumber, recaptchaVerifier)
    );
  }

  changePhoneNumberVerifySms(verificationCode) {
    const credential = firebase.auth.PhoneAuthProvider.credential(
      this.windowService.windowRef.confirmationResult,
      verificationCode
    );
    return from(this.firebaseUser.value.updatePhoneNumber(credential));
  }
}
